import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Paper,
  Stack,
  Container,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  Grid
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import BusinessIcon from '@mui/icons-material/Business'
import { supabase } from '../../supabaseClient'
import louwIcon from "../../assets/icons/louw-icon.png"
import { useTranslations } from '../../constants/translations/CaseTranslation'

const ContactRegister = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [language, setLanguage] = useState('spanish')
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const lang = params.get('lang')
    if (lang === 'en') {
      setLanguage('english')
    } else {
      setLanguage('spanish') // por defecto español
    }
  }, [])

  const t = useTranslations(language);

  const roleOptions = [
    { 
      label: 'Despacho - Equipo legal', 
      value: 'office',
      description: 'Despacho de abogados que requiere gestión integral'
    },
  /*   { 
      label: 'Cliente - Gestión de casos', 
      value: 'client',
      description: 'Para gestionar mis casos y documentos legales'
    },
    { 
      label: 'Abogado - Profesional independiente', 
      value: 'lawyer',
      description: 'Profesional del derecho que necesita gestionar su práctica'
    },
    { 
      label: 'Otro - Especificar en descripción', 
      value: 'other',
      description: 'Otro tipo de usuario o necesidad específica'
    } */
  ]

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    role: '',
    description: ''
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleChange = (field) => (event) => {
    const value = event.target.value
    setForm({ ...form, [field]: value })
    setError(null) // Limpiar errores al cambiar campos
  }

  const validateForm = () => {
    if (!form.full_name.trim()) {
      setError(t.fullNameRequired)
      return false
    }
    
    if (!form.email.trim()) {
      setError(t.emailRequired)
      return false
    }
    
    // Validación básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) {
      setError(t.emailInvalid)
      return false
    }
    
    if (!form.role) {
      setError(t.userTypeRequired)
      return false
    }
    
    if (!form.description.trim()) {
      setError(t.descriptionRequired)
      return false
    }
    
    if (form.description.trim().length < 20) {
      setError(t.descriptionMinLength)
      return false
    }

    return true
  }

  const handleSubmitConsultation = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      // Validar formulario
      if (!validateForm()) {
        setLoading(false)
        return
      }

      // Llamar a la edge function
      const { data, error: functionError } = await supabase.functions.invoke('new-client-consultation', {
        body: {
          email: form.email.trim(),
          full_name: form.full_name.trim(),
          description: form.description.trim(),
          role: form.role
        }
      })

      if (functionError) {
        throw new Error(functionError.message || 'Error al enviar la consulta')
      }

      if (data && data.error) {
        throw new Error(data.error)
      }

      // Éxito
      setSuccess(true)
      
      // Resetear formulario
      setForm({
        full_name: '',
        email: '',
        role: '',
        description: ''
      })

    } catch (error) {
      console.error('Error al enviar consulta:', error)
      setError(error.message || 'Error al enviar la consulta. Por favor, inténtelo de nuevo.')
    } finally {
      setLoading(false)
    }
  }

  const selectedRoleInfo = roleOptions.find(option => option.value === form.role)

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Box sx={{ 
        maxWidth: { xs: '100%', sm: 600, md: 700 }, 
        mx: 'auto', 
        px: { xs: 1, sm: 2 } 
      }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 2
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
            <Avatar alt="Louw APP Icon" src={louwIcon} sx={{ width: 70, height: 70, backgroundColor: 'black',  mx: 'auto', mb: 2}}/>
            {/* <BusinessIcon 
              sx={{ 
                fontSize: { xs: 40, sm: 48 }, 
                color: '#3d475d', 
                mb: 1 
              }} 
            /> */}
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              gutterBottom 
              fontWeight="bold"
              sx={{ color: '#3d475d' }}
            >
              {t.contactRegisterTitle}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                mb: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              {t.contactRegisterSubtitle}
            </Typography>
          </Box>

          {/* Mensajes de estado */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t.consultationSentSuccess}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t.teamWillContact}
              </Typography>
            </Alert>
          )}

          <form onSubmit={handleSubmitConsultation}>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              
              <TextField
                label={t.fullNameLabel}
                fullWidth
                required
                value={form.full_name}
                onChange={handleChange('full_name')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
                placeholder={t.fullNamePlaceholder}
                helperText={t.fullNameHelper}
              />

              <TextField
                label={t.emailLabel}
                fullWidth
                required
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
                placeholder={t.emailPlaceholder}
                helperText={t.emailHelper}
              />

              <FormControl fullWidth required>
                <InputLabel 
                  id="role-select-label"
                  size={isMobile ? "small" : "normal"}
                >
                  {t.userTypeLabel}
                </InputLabel>
                <Select
                  labelId="role-select-label"
                  value={form.role}
                  label={t.userTypeLabel}
                  onChange={handleChange('role')}
                  size={isMobile ? "small" : "medium"}
                  disabled={loading}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {selectedRoleInfo && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                    {selectedRoleInfo.description}
                  </Typography>
                )}
              </FormControl>

              <TextField
                label={t.descriptionLabel}
                fullWidth
                required
                multiline
                rows={isMobile ? 4 : 5}
                value={form.description}
                onChange={handleChange('description')}
                size={isMobile ? "small" : "medium"}
                disabled={loading}
                placeholder={t.descriptionPlaceholder}
                helperText={`${form.description.length}/500 ${t.descriptionHelper}`}
                inputProps={{
                  maxLength: 500
                }}
              />

              <Divider sx={{ my: { xs: 2, sm: 3 } }} />

              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: { xs: 2, sm: 3 }, 
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  <strong>{t.whatHappensNext}</strong>
                  <br />
                  {t.whatHappensNextDesc}
                </Typography>
              </Box>

              <Button 
                type="submit" 
                variant="contained" 
                fullWidth
                size={isMobile ? "medium" : "large"}
                disabled={loading || success}
                startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
                sx={{ 
                  mt: { xs: 3, sm: 4 },
                  py: { xs: 1.5, sm: 2 },
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  backgroundColor: '#3d475d',
                  '&:hover': {
                    backgroundColor: '#2c3446'
                  }
                }}
              >
                {loading ? t.sendingConsultation : t.sendRequest}
              </Button>

              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  textAlign: 'center',
                  mt: 2
                }}
              >
                {t.formConsentText}
              </Typography>
            </Stack>
          </form>
        </Paper>
      </Box>
    </Container>
  )
}

export default ContactRegister;